import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { FileText, Upload, Eye, X, Loader2, Download } from 'lucide-react';

const CertificateGeneratorModal = ({ eventId, isOpen, onClose }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const { showSuccess, showError } = useToast();

  // Preview certificate mutation
  const previewCertificateMutation = useMutation({
    mutationFn: (url) => ApiService.previewCertificate(eventId, url),
    onMutate: () => {
      setIsPreviewing(true);
    },
    onSuccess: (blob) => {
      const previewUrl = URL.createObjectURL(blob);
      setCertificatePreview(previewUrl);
      setIsPreviewing(false);
      showSuccess('Certificate preview generated successfully!');
    },
    onError: (error) => {
      setIsPreviewing(false);
      showError('Failed to generate certificate preview. Please try again.');
      console.error('Preview certificate error:', error);
    }
  });

  // Generate certificates mutation
  const generateCertificatesMutation = useMutation({
    mutationFn: (data) => ApiService.generateCertificates(eventId, data),
    onMutate: () => {
      setIsGenerating(true);
    },
    onSuccess: () => {
      setIsGenerating(false);
      showSuccess('Certificates generated successfully!');
      handleClose();
    },
    onError: (error) => {
      setIsGenerating(false);
      showError('Failed to generate certificates. Please try again.');
      console.error('Generate certificates error:', error);
    }
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        showError('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handlePreview = () => {
    // Preview works with or without certificate URL
    // If no URL provided, it will use the default template
    previewCertificateMutation.mutate(certificateUrl || null);
  };

  const handleGenerate = () => {
    if (!csvFile) {
      showError('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('storeInLocal', 'false');
    formData.append('certificateUrl', certificateUrl);

    generateCertificatesMutation.mutate(formData);
  };

  const handleClose = () => {
    setCsvFile(null);
    setCertificateUrl('');
    setCertificatePreview(null);
    setIsGenerating(false);
    setIsPreviewing(false);
    onClose();
  };

  const handleDownloadPreview = () => {
    if (certificatePreview) {
      const link = document.createElement('a');
      link.href = certificatePreview;
      link.download = 'certificate_preview.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Generate Certificates</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pb-6 space-y-6">
          {/* File Upload Section */}
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className="text-sm font-medium">CSV File *</Label>
              <p className="text-sm text-gray-600">
                Upload a CSV file containing participant data for certificate generation
              </p>
              <div className="flex items-center space-x-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="flex-1"
                />
                {csvFile && (
                  <div className="flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-md">
                    <FileText className="w-4 h-4" />
                    <span>{csvFile.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Certificate URL (Optional)</Label>
              <p className="text-sm text-gray-600">
                URL to a custom certificate template. Leave empty to use default template. You can preview the certificate with or without this URL.
              </p>
              <Input
                type="url"
                value={certificateUrl}
                onChange={(e) => setCertificateUrl(e.target.value)}
                placeholder="https://example.com/certificate-template.pdf"
                className="w-full"
              />
            </div>
          </div>

          {/* Preview Section */}
          {certificatePreview && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-medium text-green-900 mb-3">Certificate Preview:</h4>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPreview}
                  className="flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Preview</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(certificatePreview, '_blank')}
                  className="flex items-center space-x-2"
                >
                  <Eye className="w-4 h-4" />
                  <span>View in New Tab</span>
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={isPreviewing}
                className="flex items-center space-x-2"
              >
                {isPreviewing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                <span>{isPreviewing ? 'Generating Preview...' : 'Show Certificate Preview'}</span>
              </Button>
            </div>

            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={handleClose}
                className="flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!csvFile || isGenerating}
                className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span>{isGenerating ? 'Generating Certificates...' : 'Generate Certificates'}</span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CertificateGeneratorModal;
